import { apiClient, PaginatedResponse } from './api';

// Types pour les véhicules
export interface Vehicle {
  id: string;
  license_plate: string;
  brand?: string;
  model?: string;
  year?: number;
  vin?: string;
  capacity_weight?: number;
  capacity_volume?: number;
  fuel_type?: string;
  status: 'active' | 'maintenance' | 'out_of_service';
  last_maintenance?: string;
  next_maintenance?: string;
  mileage: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  assigned_driver_id?: string;
  driver_first_name?: string;
  driver_last_name?: string;
}

export interface CreateVehicleData {
  license_plate: string;
  brand?: string;
  model?: string;
  year?: number;
  vin?: string;
  capacity_weight?: number;
  capacity_volume?: number;
  fuel_type?: string;
  status?: 'active' | 'maintenance' | 'out_of_service';
  mileage?: number;
  metadata?: Record<string, any>;
}

export interface UpdateVehicleData {
  license_plate?: string;
  brand?: string;
  model?: string;
  year?: number;
  vin?: string;
  capacity_weight?: number;
  capacity_volume?: number;
  fuel_type?: string;
  status?: 'active' | 'maintenance' | 'out_of_service';
  last_maintenance?: string;
  next_maintenance?: string;
  mileage?: number;
  metadata?: Record<string, any>;
}

export interface VehicleFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'maintenance' | 'out_of_service';
  brand?: string;
  fuel_type?: string;
  maintenance_due?: boolean;
}

export interface MaintenanceRecord {
  id: string;
  vehicle_id: string;
  date: string;
  type: 'routine' | 'repair' | 'inspection';
  description: string;
  cost?: number;
  mileage?: number;
  next_maintenance_date?: string;
  performed_by?: string;
  notes?: string;
  created_at: string;
}

export interface VehicleStats {
  total_vehicles: number;
  active_vehicles: number;
  maintenance_vehicles: number;
  out_of_service_vehicles: number;
  average_mileage: number;
  maintenance_due_count: number;
  fuel_consumption_monthly?: number;
  utilization_rate?: number;
}

/**
 * Service pour la gestion des véhicules (Admin Entreprise)
 */
export class VehicleService {
  private readonly baseUrl = '/admin/vehicles';

  /**
   * Récupérer la liste des véhicules avec filtres et pagination
   */
  async getVehicles(filters: VehicleFilters = {}): Promise<PaginatedResponse<Vehicle>> {
    const url = apiClient.buildUrl(this.baseUrl, filters);
    return apiClient.get<PaginatedResponse<Vehicle>>(url);
  }

  /**
   * Récupérer un véhicule par son ID
   */
  async getVehicle(id: string): Promise<Vehicle> {
    return apiClient.get<Vehicle>(`${this.baseUrl}/${id}`);
  }

  /**
   * Créer un nouveau véhicule
   */
  async createVehicle(data: CreateVehicleData): Promise<Vehicle> {
    return apiClient.post<Vehicle>(this.baseUrl, data);
  }

  /**
   * Mettre à jour un véhicule
   */
  async updateVehicle(id: string, data: UpdateVehicleData): Promise<Vehicle> {
    return apiClient.put<Vehicle>(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Supprimer un véhicule
   */
  async deleteVehicle(id: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Récupérer les statistiques des véhicules
   */
  async getVehicleStats(): Promise<VehicleStats> {
    return apiClient.get<VehicleStats>(`${this.baseUrl}/stats`);
  }

  /**
   * Récupérer l'historique de maintenance d'un véhicule
   */
  async getMaintenanceHistory(vehicleId: string): Promise<MaintenanceRecord[]> {
    return apiClient.get<MaintenanceRecord[]>(`${this.baseUrl}/${vehicleId}/maintenance`);
  }

  /**
   * Ajouter un enregistrement de maintenance
   */
  async addMaintenanceRecord(vehicleId: string, data: Omit<MaintenanceRecord, 'id' | 'vehicle_id' | 'created_at'>): Promise<MaintenanceRecord> {
    return apiClient.post<MaintenanceRecord>(`${this.baseUrl}/${vehicleId}/maintenance`, data);
  }

  /**
   * Mettre un véhicule en maintenance
   */
  async setVehicleInMaintenance(id: string, maintenanceData?: {
    description: string;
    estimated_completion?: string;
  }): Promise<Vehicle> {
    return this.updateVehicle(id, { 
      status: 'maintenance',
      metadata: {
        maintenance_reason: maintenanceData?.description,
        maintenance_start: new Date().toISOString(),
        estimated_completion: maintenanceData?.estimated_completion,
      }
    });
  }

  /**
   * Remettre un véhicule en service
   */
  async setVehicleActive(id: string): Promise<Vehicle> {
    return this.updateVehicle(id, { 
      status: 'active',
      metadata: {
        maintenance_completed: new Date().toISOString(),
      }
    });
  }

  /**
   * Mettre un véhicule hors service
   */
  async setVehicleOutOfService(id: string, reason?: string): Promise<Vehicle> {
    return this.updateVehicle(id, { 
      status: 'out_of_service',
      metadata: {
        out_of_service_reason: reason,
        out_of_service_date: new Date().toISOString(),
      }
    });
  }

  /**
   * Récupérer les véhicules nécessitant une maintenance
   */
  async getVehiclesDueMaintenance(): Promise<Vehicle[]> {
    const response = await this.getVehicles({ 
      maintenance_due: true,
      limit: 100 
    });
    return response.data;
  }

  /**
   * Récupérer les véhicules disponibles (actifs et non assignés)
   */
  async getAvailableVehicles(): Promise<Vehicle[]> {
    const response = await this.getVehicles({ 
      status: 'active',
      limit: 100 
    });
    return response.data.filter(vehicle => !vehicle.assigned_driver_id);
  }

  /**
   * Rechercher des véhicules par plaque ou modèle
   */
  async searchVehicles(query: string, limit: number = 10): Promise<Vehicle[]> {
    const response = await this.getVehicles({ 
      search: query, 
      limit,
      page: 1 
    });
    return response.data;
  }

  /**
   * Assigner un chauffeur à un véhicule
   */
  async assignDriver(vehicleId: string, driverId: string): Promise<Vehicle> {
    return apiClient.put<Vehicle>(`${this.baseUrl}/${vehicleId}/assign-driver`, {
      driver_id: driverId
    });
  }

  /**
   * Désassigner un chauffeur d'un véhicule
   */
  async unassignDriver(vehicleId: string): Promise<Vehicle> {
    return apiClient.put<Vehicle>(`${this.baseUrl}/${vehicleId}/unassign-driver`);
  }

  /**
   * Mettre à jour le kilométrage d'un véhicule
   */
  async updateMileage(vehicleId: string, mileage: number): Promise<Vehicle> {
    return this.updateVehicle(vehicleId, { mileage });
  }

  /**
   * Valider les données d'un véhicule
   */
  validateVehicleData(data: Partial<CreateVehicleData | UpdateVehicleData>): string[] {
    const errors: string[] = [];

    if ('license_plate' in data && data.license_plate) {
      if (data.license_plate.length < 2) {
        errors.push('La plaque d\'immatriculation doit contenir au moins 2 caractères');
      }
    }

    if ('year' in data && data.year) {
      const currentYear = new Date().getFullYear();
      if (data.year < 1900 || data.year > currentYear + 1) {
        errors.push(`L'année doit être entre 1900 et ${currentYear + 1}`);
      }
    }

    if ('vin' in data && data.vin && data.vin.length !== 17) {
      errors.push('Le numéro VIN doit contenir exactement 17 caractères');
    }

    if ('capacity_weight' in data && data.capacity_weight && data.capacity_weight <= 0) {
      errors.push('Le poids maximum doit être supérieur à 0');
    }

    if ('capacity_volume' in data && data.capacity_volume && data.capacity_volume <= 0) {
      errors.push('Le volume maximum doit être supérieur à 0');
    }

    if ('mileage' in data && data.mileage && data.mileage < 0) {
      errors.push('Le kilométrage ne peut pas être négatif');
    }

    return errors;
  }

  /**
   * Formater un véhicule pour l'affichage
   */
  formatVehicleForDisplay(vehicle: Vehicle): {
    displayName: string;
    status: string;
    statusColor: string;
    specifications: string;
    maintenanceStatus: string;
    utilization: string;
  } {
    const statusMap = {
      active: { text: 'Actif', color: 'green' },
      maintenance: { text: 'Maintenance', color: 'orange' },
      out_of_service: { text: 'Hors service', color: 'red' },
    };

    const status = statusMap[vehicle.status] || { text: vehicle.status, color: 'default' };
    
    const displayName = [
      vehicle.license_plate,
      vehicle.brand,
      vehicle.model,
      vehicle.year
    ].filter(Boolean).join(' ');

    const specs = [];
    if (vehicle.capacity_weight) specs.push(`${vehicle.capacity_weight}kg`);
    if (vehicle.capacity_volume) specs.push(`${vehicle.capacity_volume}m³`);
    if (vehicle.fuel_type) specs.push(vehicle.fuel_type);

    const maintenanceStatus = this.getMaintenanceStatus(vehicle);

    return {
      displayName,
      status: status.text,
      statusColor: status.color,
      specifications: specs.join(' • '),
      maintenanceStatus,
      utilization: vehicle.assigned_driver_id ? 'Assigné' : 'Disponible',
    };
  }

  /**
   * Calculer le statut de maintenance d'un véhicule
   */
  getMaintenanceStatus(vehicle: Vehicle): string {
    if (!vehicle.next_maintenance) {
      return 'Non planifiée';
    }

    const nextMaintenance = new Date(vehicle.next_maintenance);
    const now = new Date();
    const diffDays = Math.ceil((nextMaintenance.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'En retard';
    } else if (diffDays <= 7) {
      return 'Due prochainement';
    } else if (diffDays <= 30) {
      return `Dans ${diffDays} jours`;
    } else {
      return 'À jour';
    }
  }

  /**
   * Calculer les métriques d'utilisation d'un véhicule
   */
  calculateVehicleMetrics(vehicle: Vehicle): {
    ageInYears: number;
    averageKmPerYear: number;
    maintenanceDue: boolean;
    efficiency: 'high' | 'medium' | 'low';
  } {
    const currentYear = new Date().getFullYear();
    const ageInYears = vehicle.year ? currentYear - vehicle.year : 0;
    const averageKmPerYear = ageInYears > 0 ? Math.round(vehicle.mileage / ageInYears) : 0;
    
    const maintenanceDue = vehicle.next_maintenance 
      ? new Date(vehicle.next_maintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      : false;

    let efficiency: 'high' | 'medium' | 'low' = 'medium';
    if (averageKmPerYear < 20000) efficiency = 'low';
    else if (averageKmPerYear > 50000) efficiency = 'high';

    return {
      ageInYears,
      averageKmPerYear,
      maintenanceDue,
      efficiency,
    };
  }

  /**
   * Exporter la liste des véhicules
   */
  async exportVehicles(filters: VehicleFilters = {}): Promise<Blob> {
    const url = apiClient.buildUrl(`${this.baseUrl}/export`, filters);
    const response = await apiClient.getAxiosInstance().get(url, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/csv' });
  }
}

// Instance singleton du service
export const vehicleService = new VehicleService();